import { createGtm } from '@gtm-support/vue-gtm'
import type { RouteLocationNormalized } from 'vue-router'
import type { GtmProduct, GtmTransaction } from '../../types/gtm'

/**
 * Clear ecommerce data in GTM dataLayer
 */
function clearEcommerce() {
    const dataLayer = window.dataLayer = window.dataLayer ?? []
    dataLayer.push({ ecommerce: null })
}

/**
 * Map a product to GA4 ecommerce item format
 */
function mapProductToItem(product: GtmProduct | GtmTransaction['items'][0], quantity: number) {
    return {
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        item_brand: product.brand,
        item_category: product.category,
        item_variant: product.variant,
        quantity: quantity
    }
}

/**
 * Generate list ID and name for GA4 ecommerce events
 */
function getListIdentifiers(listName: string) {
    return {
        item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
        item_list_name: listName
    }
}

/**
 * Initialize Google Tag Manager
 * @returns void
 */
export function useGtm() {
    const nuxtApp = useNuxtApp()
    const runtimeConfig = useRuntimeConfig().public
    const router = useRouter()

    // Early return if GTM is explicitly disabled
    if (runtimeConfig.gtmPwaGtmEnabled != null && runtimeConfig.gtmPwaGtmEnabled === 'disabled') {
        return
    }

    // Check if container ID is provided
    if (runtimeConfig.gtmPwaGtmContainerId == null || runtimeConfig.gtmPwaGtmContainerId === '') {
        console.warn('hubbleGTM: Please provide a valid container id')
        return
    }

    // Get customer state for user type tracking
    const { user: customer, isGuestSession } = useUser()

    // Get currency from session context (defaults to EUR)
    const { sessionContext } = useSessionContext()
    const getCurrency = () => sessionContext.value?.currency?.isoCode || 'EUR'

    // Determine enabled state
    let enabled = true // default
    if (runtimeConfig.gtmPwaGtmEnabled != null && runtimeConfig.gtmPwaGtmEnabled === 'enabledOnConsent') {
        enabled = false
    }

    // Build comprehensive GTM options
    const options = {
        enabled: enabled,
        id: runtimeConfig.gtmPwaGtmContainerId, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
        ...((runtimeConfig.gtmPwaGtmSource != null && runtimeConfig.gtmPwaGtmSource !== '') && { source: runtimeConfig.gtmPwaGtmSource }),
        defer: runtimeConfig.gtmPwaGtmDefer != null ? runtimeConfig.gtmPwaGtmDefer : false,
        compatibility: runtimeConfig.gtmPwaGtmCompatibility != null ? runtimeConfig.gtmPwaGtmCompatibility : false,
        ...((runtimeConfig.gtmPwaGtmNonce != null && runtimeConfig.gtmPwaGtmNonce !== '') && { nonce: runtimeConfig.gtmPwaGtmNonce }),
        debug: runtimeConfig.gtmPwaGtmDebug != null ? runtimeConfig.gtmPwaGtmDebug : false,
        ...((runtimeConfig.gtmPwaGtmTrackViewEventProperty != null && runtimeConfig.gtmPwaGtmTrackViewEventProperty !== '') && { trackViewEventProperty: runtimeConfig.gtmPwaGtmTrackViewEventProperty }),
        loadScript: true,
        vueRouter: router,
        trackOnNextTick: true,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        vueRouterAdditionalEventData: async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
            return {
                user_type: customer.value != null && !isGuestSession.value ? 'logged_in' : 'guest'
            }
        },
    }

    // Install the GTM plugin with comprehensive options
    nuxtApp.vueApp.use(createGtm(options))

    // Get GTM instance
    const gtm = nuxtApp.vueApp.config.globalProperties.$gtm

    // Add to Cart Hook
    nuxtApp.hook('nuxtware:productCard:clicked', ({ product, quantity }: { product: GtmProduct, quantity: number }) => {
        if (!gtm) return

        clearEcommerce()
        gtm.trackEvent({
            event: 'add_to_cart',
            ecommerce: {
                currency: getCurrency(),
                value: product.price * quantity,
                items: [mapProductToItem(product, quantity)]
            }
        })
    })

    // View Item Hook
    nuxtApp.hook('nuxtware:productDetailBuyBox:viewed', ({ product }: { product: GtmProduct }) => {
        if (!gtm) return

        clearEcommerce()
        gtm.trackEvent({
            event: 'view_item',
            ecommerce: {
                currency: getCurrency(),
                value: product.price,
                items: [mapProductToItem(product, 1)]
            }
        })
    })

    // Remove from Cart Hook
    nuxtApp.hook('nuxtware:cart:removed', ({ product, quantity }: { product: Omit<GtmProduct, 'category'>, quantity: number }) => {
        if (!gtm) return

        clearEcommerce()
        gtm.trackEvent({
            event: 'remove_from_cart',
            ecommerce: {
                currency: getCurrency(),
                value: product.price * quantity,
                items: [
                    {
                        item_id: product.id,
                        item_name: product.name,
                        price: product.price,
                        item_brand: product.brand,
                        item_variant: product.variant,
                        quantity: quantity
                    }
                ]
            }
        })
    })

    // Select Item Hook
    nuxtApp.hook('nuxtware:productCard:selected', ({ product, listName, position }: { product: Omit<GtmProduct, 'variant'>, listName?: string, position?: number }) => {
        if (!gtm) return

        clearEcommerce()
        gtm.trackEvent({
            event: 'select_item',
            ecommerce: {
                ...(listName && getListIdentifiers(listName)),
                items: [
                    {
                        item_id: product.id,
                        item_name: product.name,
                        price: product.price,
                        item_brand: product.brand,
                        item_category: product.category,
                        ...(position !== undefined && { index: position })
                    }
                ]
            }
        })
    })

    // View Item List Hook
    nuxtApp.hook('nuxtware:productListing:viewed', ({ products, listName }: { products: GtmProduct[], listName: string }) => {
        if (!gtm) return

        clearEcommerce()
        const items = products.map((product, index) => ({
            item_id: product.id,
            item_name: product.name,
            price: product.price,
            item_brand: product.brand,
            item_category: product.category,
            index: product.position ?? index + 1
        }))

        gtm.trackEvent({
            event: 'view_item_list',
            ecommerce: {
                ...getListIdentifiers(listName),
                items: items
            }
        })
    })

    // Purchase Hook
    nuxtApp.hook('nuxtware:checkout:purchased', ({ transaction }: { transaction: GtmTransaction }) => {
        if (!gtm) return

        clearEcommerce()
        const items = transaction.items.map((item: GtmTransaction['items'][0]) =>
            mapProductToItem(item, item.quantity)
        )



        gtm.trackEvent({
            event: 'purchase',
            ecommerce: {
                transaction_id: transaction.transactionId,
                affiliation: 'Online Store',
                value: transaction.value,
                tax: transaction.tax,
                shipping: transaction.shipping,
                currency: getCurrency(),
                coupon: transaction.coupon,
                items: items,
                enhanced_information: {
                    email: transaction.customerEmail ?? customer.value?.email,
                    phone: transaction.customerPhone,
                }
            }
        })
    })

    // Category Loaded Hook (Page view with customer context)
    nuxtApp.hook('nuxtware:category:loaded', ({ pageType, pageTitle, breadcrumbs }: { pageType: string, pageTitle: string, breadcrumbs?: Array<{ name: string, url?: string }> }) => {
        if (!gtm) return

        clearEcommerce()
        // Get customer data
        const customerId = customer.value?.id
        const customerGroup = customer.value && !isGuestSession.value ? 'loggedIn' : 'loggedOut'
        const customerEmail = customer.value?.email

        gtm.trackEvent({
            event: 'category_loaded',
            pageType: pageType,
            pageTitle: pageTitle,
            breadcrumbs: breadcrumbs || [],
            customer: {
                id: customerId,
                group: customerGroup,
                email: customerEmail
            },
            ecommerce: {}
        })
    })

    // AWIN Shopping Cart Hook (Prange only)
    nuxtApp.hook('nuxtware:awin:shopping_cart', ({ products }) => {
        if (!gtm) return

        clearEcommerce()
        gtm.trackEvent({
            event: 'shopping_cart',
            awin_products: products
        })
    })

    // AWIN Conversion Hook (Prange only)
    nuxtApp.hook('nuxtware:awin:conversion', ({ data }) => {
        if (!gtm) return

        clearEcommerce()
        gtm.trackEvent({
            event: 'awin_conversion',
            transactionTotal: data.transactionTotal,
            transactionId: data.transactionId,
            transactionPromoCode: data.transactionPromoCode,
            transactionCurrency: data.transactionCurrency,
            transactionParts: data.transactionParts,
            transactionProducts: data.products,
        })
    })
}
